<template>
  <div class="width-100">
    <h2 class="section-title">{{ $t('changeLog.title') }}</h2>

    <Filters
      :filters="computedFilters"
      filter-key="changeLog"
      :initial-data="filtersStatus.changeLog"
      :loading="filtersLoading"
    />

    <ViewActionButtons :buttons="actionButtons" />

    <TableWrapper
      :table-columns="computedColumns"
      table-key="changeLog"
      row-key="id"
      configurableColumns
      @viewChangelogDetails="openDetailsModal"
    />

    <ChangeLogDetail
      v-if="showDetailModal"
      :log="selectedRow"
      :changeLog="selectedChangeLog"
      @dialogClose="handleModalClose"
    />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import fileSaver from 'file-saver';
  import moment from 'moment';
  import { changeLogColumns, changeLogFilters } from '@/config';
  import {
    advancedEntities,
    advancedFilters,
    defaultChangeLogFilterData,
    DEFAULT_CHANGE_LOG_SORTING,
  } from '@/config/filters/changeLog';
  import acl from '@/services/acl';
  import { changeLogApi } from '@/api';

  import TableWrapper from '@/components/TableWrapper.vue';
  import Filters from '@/components/Filters.vue';
  import ViewActionButtons from '@/components/ViewActionButtons.vue';
  import abortControllerMixin from '@/mixins/abortControllerMixin';
  import filterLoadingMixin from '@/mixins/filterLoadingMixin';
  import axios from 'axios';

  import ChangeLogDetail from './ChangeLogDetail.vue';

  export default {
    name: 'changeLog',
    components: {
      TableWrapper,
      Filters,
      ViewActionButtons,
      ChangeLogDetail,
    },
    mixins: [abortControllerMixin, filterLoadingMixin],
    data() {
      return {
        changeLogColumns,
        changeLogFilters,
        showDetailModal: false,
        selectedChangeLog: null,
        selectedRow: null,
        actionButtons: [
          {
            text: this.$t('changeLog.download'),
            type: 'primary',
            permissions: [],
            action: () => {
              this.exportXlsx();
            },
          },
        ],
      };
    },
    computed: {
      ...mapGetters(['tableFilters', 'filtersStatus', 'tablesStatus']),
      hasAdvancedRevisionRows() {
        const rows = this.tablesStatus.changeLog?.rows || [];
        return rows.some((row) => row.source === 'advancedRevision');
      },
      computedColumns() {
        if (this.hasAdvancedRevisionRows) {
          return this.changeLogColumns;
        }
        const advancedRevisionColumns = [
          'source',
          'reportCode',
          'reportStatus',
          'revision',
        ];
        return Object.keys(this.changeLogColumns).reduce((columns, key) => {
          if (advancedRevisionColumns.includes(key)) {
            return columns;
          }
          return { ...columns, [key]: this.changeLogColumns[key] };
        }, {});
      },
      computedFilters() {
        let filters = [...this.changeLogFilters];
        if (acl.userCanUse(['advancedRevisionView'])) {
          filters = filters.map((f) => {
            if (f.name === 'entityName') {
              return {
                ...f,
                options: [...f.options, ...advancedEntities],
              };
            }
            return f;
          });
          filters.push(...advancedFilters);
        }
        return filters;
      },
    },
    async created() {
      const query = { ...this.$route.query };
      const hasStoredFilters = Object.values(this.filtersStatus.changeLog).some(
        (items) => items.length > 0
      );
      if (!query.filterKey && !hasStoredFilters) {
        this.applyFilters({
          filterKey: 'changeLog',
          filterData: defaultChangeLogFilterData(),
        });
      }
      if (!query.sorting && this.tablesStatus.changeLog.sorting.prop === null) {
        this.updateTableSorting({
          tableKey: 'changeLog',
          sorting: DEFAULT_CHANGE_LOG_SORTING,
        });
      }
      this.parseQueryStringStatus(query);
      this.tableRows();
    },
    beforeRouteUpdate(to, from, next) {
      this.parseQueryStringStatus(to.query);
      this.tableRows();
      next();
    },
    methods: {
      ...mapActions([
        'updateTableRows',
        'updateTableCount',
        'parseQueryStringStatus',
        'applyFilters',
        'updateTableSorting',
      ]),

      async exportXlsx() {
        try {
          const response = await changeLogApi.exportXlsx(
            this.filtersStatus.changeLog
          );
          fileSaver.saveAs(
            response.data,
            `change-log-${moment(new Date()).format('YYYY-MM-DD')}.xlsx`
          );
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              error.response?.data?.error?.message || this.$t('global.error'),
          });
        }
      },

      async tableRows() {
        const signal = this.createAbortController();
        this.filtersLoading = true;
        let aborted = false;
        try {
          const changeLogIndex = await changeLogApi.index(
            this.tableFilters('changeLog'),
            this.filtersStatus.changeLog,
            { signal }
          );
          const changeLogCount = await changeLogApi.count(
            this.filtersStatus.changeLog,
            { signal }
          );
          this.updateTableRows({
            tableKey: 'changeLog',
            rows: changeLogIndex.data,
          });
          this.updateTableCount({
            tableKey: 'changeLog',
            count: changeLogCount.data.count,
          });
        } catch (error) {
          if (axios.isCancel(error)) {
            aborted = true;
            return;
          }
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              error.response?.data?.error?.message || this.$t('global.error'),
          });
        } finally {
          if (!aborted) {
            this.filtersLoading = false;
          }
        }
      },
      async openDetailsModal(row) {
        try {
          this.selectedRow = row;
          const response = await changeLogApi.getDetails(row.id);
          this.selectedChangeLog = response.data;
          this.showDetailModal = true;
        } catch (error) {
          if (error.response && error.response.status === 403) {
            this.$message.error(this.$t('global.noPermissonRoute'));
          } else {
            this.$message.error(this.$t('global.error'));
          }
        }
      },
      handleModalClose() {
        this.showDetailModal = false;
        this.selectedRow = null;
        this.selectedChangeLog = null;
      },
    },
  };
</script>
